import { useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import md5 from 'js-md5';

import { brand } from '#/config/brand';
import { ROUTES } from '#/config/routes';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { Button } from '#/modules/commonComponents/button';
import BrandLogo from '#/modules/infra/components/BrandLogo';
import { userLogin } from '#/modules/infra/redux/infraSlice';
import { consumePendingSessionExitToast } from '#/utils/auth/sessionExit';
import { getAccess } from '#/utils/auth/tokenStorage';

const loginSchema = z.object({
  account: z.string().min(1, '帳號不能為空'),
  password: z.string().min(1, '密碼不能為空'),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const access = useSelector((state) => state.infra.access);
  const isSubmitting = useSelector((state) => state.infra.isSubmitting);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      account: '',
      password: '',
    },
  });

  useEffect(() => {
    consumePendingSessionExitToast();
  }, []);

  useEffect(() => {
    if (access) {
      navigate(ROUTES.home, { replace: true });
    }
  }, [access, navigate]);

  if (access || getAccess()) {
    return <Navigate to={ROUTES.home} replace />;
  }

  function onSubmit(data) {
    dispatch(userLogin({
      account: data.account,
      password: md5(data.password),
    }));
  }

  return (
    <div
      className="
        min-h-screen
        bg-radial-[at_105%_105%]
        from-sky-200 via-blue-500 to-accent-900 to-100%
      "
    >
      <div className="pt-20">
        <div className="flex flex-col items-center justify-center gap-7.5 md:flex-row md:gap-2.5">
          <BrandLogo className="h-20 md:h-10" />
          <h1 className="text-center text-2xl font-bold text-white">
            {brand.companyName}
            {' '}
            ERP 管理系統
          </h1>
        </div>
        <div className="mx-auto mt-10 max-w-md px-5 md:mt-30">
          <form
            className="
              grid grid-cols-1 gap-5 rounded-lg bg-panel p-5
              shadow shadow-gray-300
            "
            onSubmit={handleSubmit(onSubmit)}
          >
            <FloatingInput
              id="account"
              type="text"
              label="帳號"
              register={register}
              errors={errors}
              autoComplete="username"
              onKeyDown={() => {}}
            />
            <FloatingInput
              id="password"
              type="password"
              label="密碼"
              register={register}
              errors={errors}
              autoComplete="current-password"
              isVisible
              onKeyDown={() => {}}
            />
            <div className="flex items-center justify-center gap-5">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? '登入中...' : '登入'}
              </Button>
              <Button type="reset" variant="secondary">
                清除
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
